'use client';

import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import type { TimePickerProps } from '@mui/x-date-pickers/TimePicker';
import dayjs, { Dayjs } from 'dayjs';
import { useMemo } from 'react';
import { FieldValues, FieldPath, Controller, ControllerProps } from 'react-hook-form';

import { DownArrowIcon } from '../../../icons';

type TimePickerFormProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = Omit<
  TimePickerProps<Dayjs>,
  'value' | 'onChange' | 'renderInput'
> &
  Pick<ControllerProps<TFieldValues, TName>, 'name' | 'control'> & {
    placeholderMode?: 'from' | 'to' | 'default';
  };

export const TimePickerForm = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  control,
  placeholderMode,
  ...props
}: TimePickerFormProps<TFieldValues, TName>) => {
  const placeholder = useMemo(() => {
    switch (placeholderMode) {
      case 'from':
        return '00:00:00';
      case 'to':
        // const now = dayjs();
        // return now.format('HH.mm.ss');
        return '23:59:59';
      default:
        return 'hh.mm.ss';
    }
  }, [placeholderMode]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, ...fields }, fieldState: { error } }) => (
        <TimePicker
          ampm={false}
          views={['hours', 'minutes', 'seconds']}
          {...props}
          {...fields}
          value={value ? (dayjs.isDayjs(value) ? value : dayjs(value)) : null}
          slots={{
            openPickerIcon: (props) => <DownArrowIcon {...props} color={'#fff'} />,
          }}
          slotProps={{
            textField: {
              placeholder,
              error: error != null,
              helperText: error?.message,
            },
          }}
        />
      )}
    />
  );
};
